/**
 * UsersTab — user management (roles, plans, password resets, deletion).
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, ScrollView, Platform, Dimensions } from 'react-native';
import { db, getAdminAuthHeaders } from '../firebaseConfig';
import { collection, doc, onSnapshot, orderBy, query, updateDoc, serverTimestamp } from 'firebase/firestore';
import Constants from 'expo-constants';
import { showToast } from '../utils/toast';
import { adminStyles as styles } from './adminStyles';

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPlan, setNewUserPlan] = useState('FREE');
  const [newUserRole, setNewUserRole] = useState('user');
  const [newUserSuspended, setNewUserSuspended] = useState(false);
  const [newUserAlpha, setNewUserAlpha] = useState(false);
  const [newUserBeta, setNewUserBeta] = useState(false);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [expandedUsers, setExpandedUsers] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all'); // 'all' | 'admin' | 'user'
  const [filterTier, setFilterTier] = useState('all'); // 'all' | 'alpha' | 'beta'
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null); // { uid, resetLink, emailSent }
  const [resetLinkModal, setResetLinkModal] = useState(null); // { email, link } shown after password reset
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // uid of user pending delete confirmation
  const [welcomeModal, setWelcomeModal] = useState(null); // { uid, email, displayName, tier, planType }
  const [welcomeSending, setWelcomeSending] = useState(false);

  // Responsive layout
  const windowWidth = Dimensions.get('window').width;

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setUsers(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const setRole = async (uid, role) => {
    try {
      await updateDoc(doc(db, 'users', uid), { userType: role, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set role', e);
    }
  };

  const setPlan = async (uid, planType, status = 'active') => {
    try {
      await updateDoc(doc(db, 'users', uid), { subscription: { planType, subscriptionStatus: status, subscriptionUpdatedAt: new Date().toISOString() }, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set plan', e);
    }
  };

  const setSuspended = async (uid, suspended) => {
    try {
      await updateDoc(doc(db, 'users', uid), { suspended: !!suspended, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set suspended', e);
    }
  };

  const setAlphaAccess = async (uid, hasAccess) => {
    try {
      await updateDoc(doc(db, 'users', uid), { alphaAccess: !!hasAccess, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set alpha access', e);
    }
  };

  const setBetaAccessUser = async (uid, hasAccess) => {
    try {
      await updateDoc(doc(db, 'users', uid), { betaAccess: !!hasAccess, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set beta access', e);
    }
  };

  const PROTECTED_EMAILS = ['trevorhawkins@dsignable.com'];

  const deleteUser = async (userId, userEmail) => {
    setConfirmDeleteId(null);
    try {
      const base = getFunctionsBase();
      if (!base) throw new Error('Functions base URL not configured');
      const res = await fetch(`${base}/adminDeleteUser`, { method: 'POST', headers: await getAdminAuthHeaders(), body: JSON.stringify({ uid: userId, email: userEmail }) });
      const text = await res.text();
      if (!res.ok) throw new Error(text);
      showToast(`Deleted ${userEmail || userId}`, 'success');
    } catch (e) {
      console.error('Failed to delete user', e);
      showToast(`Error deleting user: ${e.message || 'Unknown error'}`, 'error');
    }
  };

  const getFunctionsBase = () => (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  );

  const sendPasswordResetAdmin = async (email) => {
    const base = getFunctionsBase();
    if (!base) { showToast('Functions base URL not configured.', 'error'); return; }
    try {
      const res = await fetch(`${base}/adminSendPasswordReset`, {
        method: 'POST',
        headers: await getAdminAuthHeaders(),
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      // Show result — link is always returned even if email sending failed
      setResetLinkModal({
        email,
        link: data.resetLink,
        emailSent: data.emailSent,
        emailNote: data.emailNote,
      });
      showToast(data.emailSent ? `Reset email sent to ${email}` : `Reset link generated for ${email}`, 'success');
    } catch (e) {
      showToast(`Password reset failed: ${e.message}`, 'error');
    }
  };

  const sendWelcomeEmail = async () => {
    if (!welcomeModal) return;
    const base = getFunctionsBase();
    if (!base) { showToast('Functions base URL not configured.', 'error'); return; }
    setWelcomeSending(true);
    try {
      const res = await fetch(`${base}/adminSendWelcomeEmail`, {
        method: 'POST',
        headers: await getAdminAuthHeaders(),
        body: JSON.stringify({
          uid: welcomeModal.uid,
          email: welcomeModal.email,
          tier: welcomeModal.tier,
          planType: welcomeModal.planType,
          adminNote: welcomeModal.adminNote || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setWelcomeModal(null);
      showToast(
        data.emailSent
          ? `Welcome email sent to ${welcomeModal.email}`
          : `Tier/plan updated for ${welcomeModal.email} (email not sent — SMTP not configured)`,
        data.emailSent ? 'success' : 'info'
      );
    } catch (e) {
      showToast(`Failed to send welcome: ${e.message}`, 'error');
    } finally {
      setWelcomeSending(false);
    }
  };

  const createUserDoc = async () => {
    setCreateError('');
    setCreateSuccess(null);
    if (!newUserEmail.trim()) { setCreateError('Email is required.'); return; }
    setCreateLoading(true);
    try {
      const base = getFunctionsBase();
      if (!base) throw new Error('Functions base URL not configured');
      const payload = {
        email: newUserEmail.trim(),
        role: newUserRole,
        planType: newUserPlan,
        suspended: !!newUserSuspended,
        alphaAccess: !!newUserAlpha,
        betaAccess: !!newUserBeta,
        password: newUserPassword.trim() || undefined,
        sendReset: true,
      };
      const res = await fetch(`${base}/adminCreateUserAndInvite`, {
        method: 'POST',
        headers: await getAdminAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      // Show success state with the reset link so admin can copy/share it
      setCreateSuccess({ email: newUserEmail.trim(), uid: data.uid, resetLink: data.resetLink, emailSent: data.emailSent, emailNote: data.emailNote });
      setNewUserEmail(''); setNewUserPassword(''); setNewUserPlan('FREE'); setNewUserRole('user'); setNewUserSuspended(false); setNewUserAlpha(false); setNewUserBeta(false);
    } catch (e) {
      console.warn('Failed to create user', e);
      setCreateError(e.message || 'Failed to create user.');
    } finally {
      setCreateLoading(false);
    }
  };

  // Filtered + searched user list
  const filteredUsers = users.filter(u => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || (u.email || '').toLowerCase().includes(q) || (u.displayName || '').toLowerCase().includes(q);
    const matchesRole = filterRole === 'all' || (u.userType || 'user') === filterRole;
    const matchesTier = filterTier === 'all'
      || (filterTier === 'alpha' && u.alphaAccess)
      || (filterTier === 'beta' && u.betaAccess);
    return matchesSearch && matchesRole && matchesTier;
  });

  // Plan badge colors
  const planColors = { FREE: '#94a3b8', BASIC: '#60a5fa', PRO: '#a78bfa', ULTIMATE: '#fbbf24' };
  const planBg = { FREE: 'rgba(148,163,184,0.15)', BASIC: 'rgba(96,165,250,0.15)', PRO: 'rgba(167,139,250,0.15)', ULTIMATE: 'rgba(251,191,36,0.15)' };

  return (
    <View style={styles.tabWrap}>
      {/* ── Header ── */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <View>
          <Text style={styles.sectionTitle}>Users</Text>
          {!loading && (
            <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 2 }}>
              {users.length} total · {users.filter(u => u.userType === 'admin').length} admin · {users.filter(u => u.alphaAccess).length} alpha · {users.filter(u => u.betaAccess).length} beta
            </Text>
          )}
        </View>
        <TouchableOpacity
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: 'rgba(139,92,246,0.25)', borderWidth: 1, borderColor: 'rgba(139,92,246,0.6)', paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8 }}
          onPress={() => { setCreateError(''); setModalOpen(true); }}
        >
          <Text style={{ color: '#c4b5fd', fontWeight: '700', fontSize: 13 }}>+ Add User</Text>
        </TouchableOpacity>
      </View>

      {/* ── Search + Filters ── */}
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextInput
          style={{ flex: 1, minWidth: 160, backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 7, color: '#fff', fontSize: 13 }}
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search email or name…"
          placeholderTextColor="rgba(255,255,255,0.3)"
        />
        {/* Role filter */}
        <View style={{ flexDirection: 'row', gap: 4 }}>
          {[['all','All'], ['admin','Admin'], ['user','User']].map(([key, label]) => (
            <TouchableOpacity key={key} onPress={() => setFilterRole(key)}
              style={{ paddingVertical: 6, paddingHorizontal: 10, borderRadius: 6, borderWidth: 1,
                borderColor: filterRole === key ? '#a78bfa' : 'rgba(255,255,255,0.12)',
                backgroundColor: filterRole === key ? 'rgba(167,139,250,0.2)' : 'transparent' }}>
              <Text style={{ color: filterRole === key ? '#a78bfa' : 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: '600' }}>{label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        {/* Tier filter */}
        <View style={{ flexDirection: 'row', gap: 4 }}>
          {[['all','All Tiers'], ['alpha','Alpha'], ['beta','Beta']].map(([key, label]) => (
            <TouchableOpacity key={key} onPress={() => setFilterTier(key)}
              style={{ paddingVertical: 6, paddingHorizontal: 10, borderRadius: 6, borderWidth: 1,
                borderColor: filterTier === key ? (key === 'alpha' ? '#4ade80' : key === 'beta' ? '#fbbf24' : '#60a5fa') : 'rgba(255,255,255,0.12)',
                backgroundColor: filterTier === key ? (key === 'alpha' ? 'rgba(74,222,128,0.15)' : key === 'beta' ? 'rgba(251,191,36,0.15)' : 'rgba(96,165,250,0.1)') : 'transparent' }}>
              <Text style={{ color: filterTier === key ? (key === 'alpha' ? '#4ade80' : key === 'beta' ? '#fbbf24' : '#60a5fa') : 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: '600' }}>{label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* ── User List ── */}
      {loading ? (
        <Text style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', paddingVertical: 32 }}>Loading users…</Text>
      ) : filteredUsers.length === 0 ? (
        <Text style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', paddingVertical: 32 }}>No users match your filters.</Text>
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 24, gap: 8 }}>
          {filteredUsers.map((u) => {
            const isExpanded = expandedUsers[u.id];
            const plan = u?.subscription?.planType || 'FREE';
            const isAdmin = u.userType === 'admin';
            const isSuspended = !!u.suspended;
            const initials = (u.email || u.id || '?').substring(0, 2).toUpperCase();

            return (
              <View key={u.id} style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: isSuspended ? 'rgba(239,68,68,0.3)' : isAdmin ? 'rgba(167,139,250,0.25)' : 'rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' }}>

                {/* ── Card Header (always visible) ── */}
                <TouchableOpacity
                  activeOpacity={0.8}
                  onPress={() => setExpandedUsers(prev => ({ ...prev, [u.id]: !prev[u.id] }))}
                  style={{ flexDirection: 'row', alignItems: 'center', padding: 14, gap: 12 }}
                >
                  {/* Avatar */}
                  <View style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: isAdmin ? 'rgba(167,139,250,0.3)' : 'rgba(255,255,255,0.08)', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                    <Text style={{ color: isAdmin ? '#a78bfa' : 'rgba(255,255,255,0.6)', fontWeight: '700', fontSize: 13 }}>{initials}</Text>
                  </View>

                  {/* Info */}
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }} numberOfLines={1}>{u.email || u.id}</Text>
                    {u.displayName ? <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 1 }}>{u.displayName}</Text> : null}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                      {/* Role badge */}
                      <View style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: 4, backgroundColor: isAdmin ? 'rgba(167,139,250,0.2)' : 'rgba(255,255,255,0.08)' }}>
                        <Text style={{ color: isAdmin ? '#a78bfa' : 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: '700' }}>{isAdmin ? 'ADMIN' : 'USER'}</Text>
                      </View>
                      {/* Plan badge */}
                      <View style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: 4, backgroundColor: planBg[plan] || planBg.FREE }}>
                        <Text style={{ color: planColors[plan] || planColors.FREE, fontSize: 10, fontWeight: '700' }}>{plan}</Text>
                      </View>
                      {/* Alpha */}
                      {u.alphaAccess && (
                        <View style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: 4, backgroundColor: 'rgba(74,222,128,0.15)' }}>
                          <Text style={{ color: '#4ade80', fontSize: 10, fontWeight: '700' }}>ALPHA</Text>
                        </View>
                      )}
                      {/* Beta */}
                      {u.betaAccess && (
                        <View style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: 4, backgroundColor: 'rgba(251,191,36,0.15)' }}>
                          <Text style={{ color: '#fbbf24', fontSize: 10, fontWeight: '700' }}>BETA</Text>
                        </View>
                      )}
                      {/* Suspended */}
                      {isSuspended && (
                        <View style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: 4, backgroundColor: 'rgba(239,68,68,0.2)' }}>
                          <Text style={{ color: '#ef4444', fontSize: 10, fontWeight: '700' }}>SUSPENDED</Text>
                        </View>
                      )}
                    </View>
                  </View>

                  {/* Chevron */}
                  <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginLeft: 4 }}>{isExpanded ? '▲' : '▼'}</Text>
                </TouchableOpacity>

                {/* ── Expanded Actions ── */}
                {isExpanded && (
                  <View style={{ borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.07)', padding: 14, gap: 14 }}>

                    {/* Role */}
                    <View>
                      <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: '700', letterSpacing: 0.8, marginBottom: 6 }}>ROLE</Text>
                      <View style={{ flexDirection: 'row', gap: 6 }}>
                        {['user', 'admin'].map(r => (
                          <TouchableOpacity key={r}
                            onPress={() => setRole(u.id, r)}
                            style={{ paddingVertical: 6, paddingHorizontal: 14, borderRadius: 7, borderWidth: 1,
                              borderColor: (u.userType || 'user') === r ? '#a78bfa' : 'rgba(255,255,255,0.15)',
                              backgroundColor: (u.userType || 'user') === r ? 'rgba(167,139,250,0.2)' : 'transparent' }}
                          >
                            <Text style={{ color: (u.userType || 'user') === r ? '#a78bfa' : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '600', textTransform: 'capitalize' }}>{r}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>

                    {/* Plan */}
                    <View>
                      <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: '700', letterSpacing: 0.8, marginBottom: 6 }}>PLAN</Text>
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                        {[['FREE','none'], ['BASIC','active'], ['PRO','active'], ['ULTIMATE','active']].map(([p, status]) => (
                          <TouchableOpacity key={p}
                            onPress={() => setPlan(u.id, p, status)}
                            style={{ paddingVertical: 6, paddingHorizontal: 14, borderRadius: 7, borderWidth: 1,
                              borderColor: plan === p ? (planColors[p] || '#fff') : 'rgba(255,255,255,0.15)',
                              backgroundColor: plan === p ? (planBg[p] || 'transparent') : 'transparent' }}
                          >
                            <Text style={{ color: plan === p ? (planColors[p] || '#fff') : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '600' }}>{p}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>

                    {/* Access Tier */}
                    <View>
                      <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: '700', letterSpacing: 0.8, marginBottom: 6 }}>ACCESS TIER</Text>
                      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                        <TouchableOpacity
                          onPress={() => setAlphaAccess(u.id, !u.alphaAccess)}
                          style={{ paddingVertical: 6, paddingHorizontal: 14, borderRadius: 7, borderWidth: 1,
                            borderColor: u.alphaAccess ? '#4ade80' : 'rgba(255,255,255,0.15)',
                            backgroundColor: u.alphaAccess ? 'rgba(74,222,128,0.15)' : 'transparent' }}
                        >
                          <Text style={{ color: u.alphaAccess ? '#4ade80' : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '600' }}>
                            {u.alphaAccess ? 'Alpha ✓' : 'Alpha'}
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => setBetaAccessUser(u.id, !u.betaAccess)}
                          style={{ paddingVertical: 6, paddingHorizontal: 14, borderRadius: 7, borderWidth: 1,
                            borderColor: u.betaAccess ? '#fbbf24' : 'rgba(255,255,255,0.15)',
                            backgroundColor: u.betaAccess ? 'rgba(251,191,36,0.15)' : 'transparent' }}
                        >
                          <Text style={{ color: u.betaAccess ? '#fbbf24' : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '600' }}>
                            {u.betaAccess ? 'Beta ✓' : 'Beta'}
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => setSuspended(u.id, !u.suspended)}
                          style={{ paddingVertical: 6, paddingHorizontal: 14, borderRadius: 7, borderWidth: 1,
                            borderColor: isSuspended ? '#ef4444' : 'rgba(255,255,255,0.15)',
                            backgroundColor: isSuspended ? 'rgba(239,68,68,0.15)' : 'transparent' }}
                        >
                          <Text style={{ color: isSuspended ? '#ef4444' : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '600' }}>
                            {isSuspended ? 'Suspended ✓' : 'Suspend'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Danger zone */}
                    <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', paddingTop: 6, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.06)' }}>
                      {/* Welcome email */}
                      <TouchableOpacity
                        onPress={() => {
                          if (!u.email) { showToast('No email address for this user', 'error'); return; }
                          const currentTier = u.alphaAccess ? 'alpha' : u.betaAccess ? 'beta' : 'user';
                          const currentPlan = u.subscription?.planType || 'FREE';
                          setWelcomeModal({ uid: u.id, email: u.email, displayName: u.displayName || u.email, tier: currentTier, planType: currentPlan, adminNote: '' });
                        }}
                        style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(52,211,153,0.4)', backgroundColor: 'rgba(52,211,153,0.08)' }}
                      >
                        <Text style={{ color: '#34d399', fontSize: 12, fontWeight: '600' }}>Send Welcome</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => {
                          if (!u.email) { showToast('No email address for this user', 'error'); return; }
                          sendPasswordResetAdmin(u.email);
                        }}
                        style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(251,191,36,0.4)', backgroundColor: 'rgba(251,191,36,0.08)' }}
                      >
                        <Text style={{ color: '#fbbf24', fontSize: 12, fontWeight: '600' }}>Send Password Reset</Text>
                      </TouchableOpacity>
                      {!PROTECTED_EMAILS.includes((u.email || '').toLowerCase()) && (
                        confirmDeleteId === u.id ? (
                          <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>Permanently delete?</Text>
                            <TouchableOpacity
                              onPress={() => deleteUser(u.id, u.email)}
                              style={{ paddingVertical: 5, paddingHorizontal: 10, borderRadius: 7, backgroundColor: '#ef4444' }}
                            >
                              <Text style={{ color: '#fff', fontSize: 12, fontWeight: '700' }}>Yes, Delete</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              onPress={() => setConfirmDeleteId(null)}
                              style={{ paddingVertical: 5, paddingHorizontal: 10, borderRadius: 7, backgroundColor: 'rgba(255,255,255,0.1)' }}
                            >
                              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: '600' }}>Cancel</Text>
                            </TouchableOpacity>
                          </View>
                        ) : (
                          <TouchableOpacity
                            onPress={() => setConfirmDeleteId(u.id)}
                            style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(239,68,68,0.4)', backgroundColor: 'rgba(239,68,68,0.08)' }}
                          >
                            <Text style={{ color: '#ef4444', fontSize: 12, fontWeight: '600' }}>Delete User</Text>
                          </TouchableOpacity>
                        )
                      )}
                    </View>

                  </View>
                )}
              </View>
            );
          })}
        </ScrollView>
      )}

      {/* ── Create User Modal ── */}
      {modalOpen && (
        <View style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: 20 }}>
          <View style={{ width: '100%', maxWidth: 500, borderRadius: 16, backgroundColor: '#16162a', borderWidth: 1, borderColor: 'rgba(139,92,246,0.3)', overflow: 'hidden' }}>
            {/* Modal header */}
            <View style={{ padding: 20, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.08)', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 17 }}>Create New User</Text>
              <TouchableOpacity onPress={() => setModalOpen(false)} style={{ padding: 4 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={{ maxHeight: 520 }} contentContainerStyle={{ padding: 20, gap: 16 }}>
              {/* Email */}
              <View style={{ gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>EMAIL *</Text>
                <TextInput
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 9, color: '#fff', fontSize: 14 }}
                  value={newUserEmail}
                  onChangeText={setNewUserEmail}
                  placeholder="user@example.com"
                  placeholderTextColor="rgba(255,255,255,0.3)"
                  autoCapitalize="none"
                  keyboardType="email-address"
                />
              </View>

              {/* Password */}
              <View style={{ gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>TEMP PASSWORD <Text style={{ color: 'rgba(255,255,255,0.3)', fontWeight: '400' }}>(optional — invite email sent if blank)</Text></Text>
                <TextInput
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 9, color: '#fff', fontSize: 14 }}
                  value={newUserPassword}
                  onChangeText={setNewUserPassword}
                  placeholder="Leave blank to send invite email"
                  placeholderTextColor="rgba(255,255,255,0.3)"
                  secureTextEntry
                />
              </View>

              {/* Role + Plan row */}
              <View style={{ flexDirection: 'row', gap: 14 }}>
                <View style={{ flex: 1, gap: 6 }}>
                  <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>ROLE</Text>
                  <View style={{ flexDirection: 'row', gap: 6 }}>
                    {['user', 'admin'].map(r => (
                      <TouchableOpacity key={r} onPress={() => setNewUserRole(r)}
                        style={{ flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1, alignItems: 'center',
                          borderColor: newUserRole === r ? '#a78bfa' : 'rgba(255,255,255,0.15)',
                          backgroundColor: newUserRole === r ? 'rgba(167,139,250,0.2)' : 'transparent' }}>
                        <Text style={{ color: newUserRole === r ? '#a78bfa' : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600', textTransform: 'capitalize' }}>{r}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              </View>

              {/* Plan */}
              <View style={{ gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>PLAN</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {[['FREE','none'], ['BASIC','active'], ['PRO','active'], ['ULTIMATE','active']].map(([p]) => (
                    <TouchableOpacity key={p} onPress={() => setNewUserPlan(p)}
                      style={{ paddingVertical: 7, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1,
                        borderColor: newUserPlan === p ? (planColors[p] || '#fff') : 'rgba(255,255,255,0.15)',
                        backgroundColor: newUserPlan === p ? (planBg[p] || 'transparent') : 'transparent' }}>
                      <Text style={{ color: newUserPlan === p ? (planColors[p] || '#fff') : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' }}>{p}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Access tier */}
              <View style={{ gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>ACCESS TIER</Text>
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  <TouchableOpacity onPress={() => setNewUserAlpha(v => !v)}
                    style={{ flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1, alignItems: 'center',
                      borderColor: newUserAlpha ? '#4ade80' : 'rgba(255,255,255,0.15)',
                      backgroundColor: newUserAlpha ? 'rgba(74,222,128,0.15)' : 'transparent' }}>
                    <Text style={{ color: newUserAlpha ? '#4ade80' : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' }}>{newUserAlpha ? 'Alpha ✓' : 'Alpha'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setNewUserBeta(v => !v)}
                    style={{ flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1, alignItems: 'center',
                      borderColor: newUserBeta ? '#fbbf24' : 'rgba(255,255,255,0.15)',
                      backgroundColor: newUserBeta ? 'rgba(251,191,36,0.15)' : 'transparent' }}>
                    <Text style={{ color: newUserBeta ? '#fbbf24' : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' }}>{newUserBeta ? 'Beta ✓' : 'Beta'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setNewUserSuspended(v => !v)}
                    style={{ flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1, alignItems: 'center',
                      borderColor: newUserSuspended ? '#ef4444' : 'rgba(255,255,255,0.15)',
                      backgroundColor: newUserSuspended ? 'rgba(239,68,68,0.12)' : 'transparent' }}>
                    <Text style={{ color: newUserSuspended ? '#ef4444' : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' }}>{newUserSuspended ? 'Suspended ✓' : 'Suspend'}</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {createError ? <Text style={{ color: '#f87171', fontSize: 13 }}>{createError}</Text> : null}
            </ScrollView>

            {/* Success state — show reset link before closing */}
            {createSuccess && (
              <View style={{ margin: 16, padding: 14, borderRadius: 10, backgroundColor: 'rgba(74,222,128,0.08)', borderWidth: 1, borderColor: 'rgba(74,222,128,0.3)' }}>
                <Text style={{ color: '#4ade80', fontWeight: '700', fontSize: 13, marginBottom: 6 }}>User created successfully</Text>
                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 4 }}>
                  {createSuccess.emailSent ? `Invite email sent to ${createSuccess.email}` : `Email not sent (${createSuccess.emailNote || 'SMTP not configured'}).`}
                </Text>
                {createSuccess.resetLink ? (
                  <View style={{ marginTop: 8 }}>
                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 4 }}>Password reset link (copy and share):</Text>
                    <Text
                      selectable
                      style={{ color: '#a78bfa', fontSize: 11, backgroundColor: 'rgba(255,255,255,0.05)', padding: 8, borderRadius: 6, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}
                    >{createSuccess.resetLink}</Text>
                  </View>
                ) : null}
              </View>
            )}

            {/* Modal footer */}
            <View style={{ padding: 16, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)', flexDirection: 'row', justifyContent: 'flex-end', gap: 10 }}>
              <TouchableOpacity onPress={() => { setModalOpen(false); setCreateSuccess(null); setCreateError(''); }}
                style={{ paddingVertical: 9, paddingHorizontal: 18, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)' }}>
                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: '600' }}>{createSuccess ? 'Done' : 'Cancel'}</Text>
              </TouchableOpacity>
              {!createSuccess && (
                <TouchableOpacity onPress={createUserDoc} disabled={createLoading}
                  style={{ paddingVertical: 9, paddingHorizontal: 22, borderRadius: 8, backgroundColor: createLoading ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.6)', borderWidth: 1, borderColor: 'rgba(139,92,246,0.8)' }}>
                  <Text style={{ color: createLoading ? 'rgba(255,255,255,0.4)' : '#fff', fontSize: 13, fontWeight: '700' }}>{createLoading ? 'Creating…' : 'Create + Send Invite'}</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      )}

      {/* ── Welcome Email Confirmation Modal ── */}
      {welcomeModal && (
        <View style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'center', alignItems: 'center', zIndex: 1002, padding: 20 }}>
          <View style={{ width: '100%', maxWidth: 480, borderRadius: 16, backgroundColor: '#16162a', borderWidth: 1, borderColor: 'rgba(52,211,153,0.35)', padding: 28 }}>
            <Text style={{ color: '#fff', fontWeight: '800', fontSize: 18, marginBottom: 4 }}>Send Welcome Email</Text>
            <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, marginBottom: 24 }}>{welcomeModal.email}</Text>

            {/* Tier selector */}
            <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Access Tier</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 20 }}>
              {[
                { key: 'alpha', label: 'Alpha', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.5)' },
                { key: 'beta', label: 'Beta', color: '#60a5fa', bg: 'rgba(96,165,250,0.15)', border: 'rgba(96,165,250,0.5)' },
                { key: 'user', label: 'User', color: '#34d399', bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.5)' },
              ].map(({ key, label, color, bg, border }) => (
                <TouchableOpacity
                  key={key}
                  onPress={() => setWelcomeModal(m => ({ ...m, tier: key }))}
                  style={{ flex: 1, paddingVertical: 10, borderRadius: 10, borderWidth: 2,
                    borderColor: welcomeModal.tier === key ? border : 'rgba(255,255,255,0.1)',
                    backgroundColor: welcomeModal.tier === key ? bg : 'transparent',
                    alignItems: 'center' }}
                >
                  <Text style={{ color: welcomeModal.tier === key ? color : 'rgba(255,255,255,0.4)', fontWeight: '700', fontSize: 13 }}>{label}</Text>
                  {welcomeModal.tier === key && (
                    <Text style={{ color, fontSize: 10, marginTop: 2, fontWeight: '600' }}>
                      {key === 'alpha' ? 'Early access' : key === 'beta' ? 'Beta access' : 'Full access'}
                    </Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>

            {/* Plan selector */}
            <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Plan</Text>
            <View style={{ flexDirection: 'row', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
              {['FREE', 'BASIC', 'PRO', 'ULTIMATE'].map(p => (
                <TouchableOpacity
                  key={p}
                  onPress={() => setWelcomeModal(m => ({ ...m, planType: p }))}
                  style={{ paddingVertical: 7, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1.5,
                    borderColor: welcomeModal.planType === p ? (planColors[p] || '#fff') : 'rgba(255,255,255,0.1)',
                    backgroundColor: welcomeModal.planType === p ? (planBg[p] || 'rgba(255,255,255,0.08)') : 'transparent' }}
                >
                  <Text style={{ color: welcomeModal.planType === p ? (planColors[p] || '#fff') : 'rgba(255,255,255,0.4)', fontWeight: '700', fontSize: 12 }}>{p}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Optional note */}
            <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Personal Note (optional)</Text>
            <TextInput
              value={welcomeModal.adminNote || ''}
              onChangeText={v => setWelcomeModal(m => ({ ...m, adminNote: v }))}
              placeholder="Add a personal note to the welcome email…"
              placeholderTextColor="rgba(255,255,255,0.25)"
              multiline
              numberOfLines={3}
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 10, color: '#fff', fontSize: 13, minHeight: 70, marginBottom: 24 }}
            />

            {/* Summary */}
            <View style={{ padding: 12, borderRadius: 10, backgroundColor: 'rgba(52,211,153,0.07)', borderWidth: 1, borderColor: 'rgba(52,211,153,0.2)', marginBottom: 20 }}>
              <Text style={{ color: '#34d399', fontSize: 13, fontWeight: '600' }}>
                This will set {welcomeModal.email} as a{welcomeModal.tier === 'alpha' ? 'n' : ''} <Text style={{ fontWeight: '800' }}>{welcomeModal.tier.charAt(0).toUpperCase() + welcomeModal.tier.slice(1)}</Text> tester on the <Text style={{ fontWeight: '800' }}>{welcomeModal.planType}</Text> plan and send them a welcome email.
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
              <TouchableOpacity
                onPress={() => setWelcomeModal(null)}
                style={{ paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.08)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)' }}
              >
                <Text style={{ color: 'rgba(255,255,255,0.6)', fontWeight: '600', fontSize: 13 }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={sendWelcomeEmail}
                disabled={welcomeSending}
                style={{ paddingVertical: 10, paddingHorizontal: 24, borderRadius: 8, backgroundColor: welcomeSending ? 'rgba(52,211,153,0.3)' : '#34d399' }}
              >
                <Text style={{ color: welcomeSending ? 'rgba(255,255,255,0.5)' : '#0f0f17', fontWeight: '800', fontSize: 13 }}>
                  {welcomeSending ? 'Sending…' : 'Confirm & Send'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* ── Reset Link Modal (shown after Send Password Reset) ── */}
      {resetLinkModal && (
        <View style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', justifyContent: 'center', alignItems: 'center', zIndex: 1001, padding: 20 }}>
          <View style={{ width: '100%', maxWidth: 460, borderRadius: 16, backgroundColor: '#16162a', borderWidth: 1, borderColor: 'rgba(251,191,36,0.3)', padding: 24 }}>
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 16, marginBottom: 4 }}>Password Reset</Text>
            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 16 }}>{resetLinkModal.email}</Text>
            <View style={{ padding: 12, borderRadius: 8, backgroundColor: resetLinkModal.emailSent ? 'rgba(74,222,128,0.08)' : 'rgba(251,191,36,0.08)', borderWidth: 1, borderColor: resetLinkModal.emailSent ? 'rgba(74,222,128,0.3)' : 'rgba(251,191,36,0.3)', marginBottom: 16 }}>
              <Text style={{ color: resetLinkModal.emailSent ? '#4ade80' : '#fbbf24', fontWeight: '600', fontSize: 13 }}>
                {resetLinkModal.emailSent ? 'Reset email sent successfully' : `Email not sent — ${resetLinkModal.emailNote || 'SMTP not configured'}`}
              </Text>
            </View>
            {resetLinkModal.link && (
              <View style={{ marginBottom: 16 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 6 }}>Reset link (expires in 1 hour) — copy and share manually if needed:</Text>
                <Text
                  selectable
                  style={{ color: '#a78bfa', fontSize: 11, backgroundColor: 'rgba(255,255,255,0.05)', padding: 10, borderRadius: 8, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, lineHeight: 18 }}
                >{resetLinkModal.link}</Text>
              </View>
            )}
            <TouchableOpacity
              onPress={() => setResetLinkModal(null)}
              style={{ paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.1)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', alignSelf: 'flex-end' }}
            >
              <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

async function runOpticOddsRequest(path) {
  const functionsBase = (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
      || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
      || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
      || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  );
  if (!(Platform.OS === 'web' && !!functionsBase)) {
    return { status: 500, ok: false, body: { error: 'Functions base not configured. Set EXPO_PUBLIC_FUNCTIONS_BASE_URL.' } };
  }
  const url = `${functionsBase}/opticOddsProxy?path=${encodeURIComponent(path)}`;
  // The proxy only serves arbitrary paths to signed-in admins.
  const res = await fetch(url, { headers: await getAdminAuthHeaders() });
  const text = await res.text();
  let body;
  try { body = JSON.parse(text); } catch { body = text; }
  return { status: res.status, ok: res.ok, body };
}

async function runNlOdds(prompt, defaults = {}) {
  const base = (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  );
  const url = `${base}/nlOdds`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, defaults })
  });
  const text = await res.text();
  let body; try { body = JSON.parse(text); } catch { body = text; }
  return { status: res.status, ok: res.ok, body };
}

async function runSweep(params = {}) {
  const base = (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  );
  const url = `${base}/opticOddsSweep`;
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(params) });
  const text = await res.text();
  let body; try { body = JSON.parse(text); } catch { body = text; }
  return { status: res.status, ok: res.ok, body };
}

